import { gql, useQuery } from '@apollo/client';
import equals from 'fast-deep-equal';
import memoizeOne from 'memoize-one';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { ScrollPosition } from 'react-lazy-load-image-component';
import { getTourPrice } from '../../../features/multiCurrency/getTourPrice';
import { ASSET_FRAGMENT } from '../../../graphql/fragments/Asset';
import { I18NSTRING } from '../../../graphql/fragments/I18nString';
import { PRICE_FRAGMENT } from '../../../graphql/fragments/Price';
import { ProductSKU } from '../../../graphql/globalTypes';
import { addToCart } from '../../../store/app/actions';
import {
  selectCurrencyCode,
  selectIsAuthenticated,
  selectPinnedTours,
} from '../../../store/app/selectors';
import { Asset, Country, I18nInfoField, Price } from '../../../types';
import { getHomeScreenSortedTours } from './utils/getHomeScreenSortedTours';
import { TourWidgetNew } from './productWidgetNew/TourWidgetNew';
import { selectVariant } from '../../../store/googleOptimize/selectors';
import { env } from '../../../App/config/env';
import { TourWidget } from './productWidget/TourWidget';
import { EXPLORE_WEB_GetToursForToursSectionAuthed as Data } from '../toursSection/__generated__/EXPLORE_WEB_GetToursForToursSectionAuthed';

export const GET_TOURS_FOR_TOURS_SECTION_UNAUTHED = gql`
  query EXPLORE_WEB_GetToursForToursSectionUnauthed(
    $skipPresentThumbnail: Boolean!
    $playlistIDs: [ID!]
  ) {
    result: getTours(input: { playlistIDs: $playlistIDs }) {
      id
      internalReference
      trial
      published
      purchased
      nameI18n {
        ...I18nStringFragment
      }
      validityPeriodMonths
      trailLength
      timePeriod
      audioDurationMinutes
      descriptionI18n {
        ...I18nStringFragment
      }
      countries {
        id
        name
      }
      viewpoints {
        imagesCount
      }
      price {
        ...PriceFragment
      }
      pastThumbnail {
        ...AssetFragment
      }
      presentThumbnail @skip(if: $skipPresentThumbnail) {
        ...AssetFragment
      }
      activeContentPurchase {
        id
        expiresAt
      }
    }
  }

  ${ASSET_FRAGMENT}
  ${PRICE_FRAGMENT}
  ${I18NSTRING}
`;

export const GET_TOURS_FOR_TOURS_SECTION_AUTHED = gql`
  query EXPLORE_WEB_GetToursForToursSectionAuthed(
    $skipPresentThumbnail: Boolean!
    $playlistIDs: [ID!]
  ) {
    result: getTours(input: { playlistIDs: $playlistIDs }) {
      id
      internalReference
      trial
      published
      purchased
      descriptionI18n {
        ...I18nStringFragment
      }
      countries {
        id
        name
      }
      nameI18n {
        ...I18nStringFragment
      }
      viewpoints {
        imagesCount
      }
      validityPeriodMonths
      trailLength
      timePeriod
      audioDurationMinutes
      price {
        ...PriceFragment
      }
      pastThumbnail {
        ...AssetFragment
      }
      presentThumbnail @skip(if: $skipPresentThumbnail) {
        ...AssetFragment
      }
      activeContentPurchase {
        id
        expiresAt
      }
    }
  }
  ${ASSET_FRAGMENT}
  ${PRICE_FRAGMENT}
  ${I18NSTRING}
`;

export type Tour = {
  id: string;
  internalReference: string;
  trial: boolean;
  published: boolean;
  purchased: boolean;
  nameI18n: I18nInfoField;
  validityPeriodMonths: number | null;
  trailLength: number | null;
  timePeriod: string | null;
  audioDurationMinutes: number | null;
  price: Price;
  pastThumbnail: Asset | null;
  presentThumbnail: Asset | null;
  descriptionI18n: I18nInfoField;
  countries: Country[];
  viewpoints: Array<{ imagesCount: number }>;
  activeContentPurchase?: {
    id: string;
    expiresAt: any | null;
  } | null;
};

interface Props {
  opensItineraryPage: boolean;
  defaultTours?: Tour[];
  scrollPosition?: ScrollPosition;
  playlistIDs?: string[];
}

//@ts-ignore
export const Tours: React.FC<Props> = ({
  defaultTours = [],
  opensItineraryPage,
  scrollPosition,
  playlistIDs,
}) => {
  const variant = useSelector(selectVariant(env.GO_EXP_ID_FOR_LIBRARY_PAGE));
  const pinnedTours = useSelector(selectPinnedTours);
  const authed = useSelector(selectIsAuthenticated);
  const currencyCode = useSelector(selectCurrencyCode);
  const dispatch = useDispatch();

  const query = authed
    ? GET_TOURS_FOR_TOURS_SECTION_AUTHED
    : GET_TOURS_FOR_TOURS_SECTION_UNAUTHED;

  const { data } = useQuery<Data, null>(query, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    // @ts-ignore - Type '{ skipPresentThumbnail: boolean; }' is not assignable to type 'null | undefined'.
    variables: {
      skipPresentThumbnail: isMobile,
      playlistIDs,
    },
  });

  const targetTours = data?.result;
  const tours = memoizedGetSortedTours<Tour>(
    (targetTours as Tour[]) || defaultTours,
    pinnedTours
  );

  const hasPinnedTours = pinnedTours.length > 0;

  const toursWithPinnedAndProcessedPrice = tours.map((tour) => {
    const tourPrice = getTourPrice({ tour, currencyCode });

    return {
      ...tour,
      pinned: Boolean(
        hasPinnedTours &&
          pinnedTours.find((t) => t.tourID === tour.id)?.viewpointID
      ),
      unitPrice: tourPrice.price,
      currencyCode: tourPrice.currencyCode,
      priceInCents: true,
    };
  });

  const buyTour = (tourID: string) => {
    const tour = toursWithPinnedAndProcessedPrice.find((t) => t.id === tourID);

    if (tour) {
      const tourPrice = getTourPrice({ tour, currencyCode });

      dispatch(
        addToCart({
          productID: tour.id,
          productSKU: ProductSKU.PLAYLIST,
          currencyCode: tourPrice.currencyCode,
          count: 1,
        })
      );
    }
  };
  const Widget = variant === '0' ? TourWidget : TourWidgetNew;
  return toursWithPinnedAndProcessedPrice.map((tour) => (
    <Widget
      key={tour.id}
      tour={tour}
      buyTour={buyTour}
      scrollPosition={scrollPosition}
      opensItineraryPage={opensItineraryPage}
    />
    // <TourWidgetNew
    //   key={tour.id}
    //   tour={tour}
    //   buyTour={buyTour}
    //   scrollPosition={scrollPosition}
    //   opensItineraryPage={opensItineraryPage}
    // />
  ));
};

const memoizedGetSortedTours = memoizeOne(getHomeScreenSortedTours, equals);
